<template>
  <header
    class="nav"
    :class="{ 'is-scrolled': isScrolled, 'is-open': isMenuOpen }"
  >
    <div class="nav__topbar">
      <span
        class="nav__open"
        :class="`is-${openState}`"
        :title="openTitle"
      >
        <span class="nav__open-dot" />
        {{ openLabel }}
      </span>
    </div>

    <div class="nav__bar">
      <div class="nav__inner container">
        <a
          class="nav__logo"
          href="/"
          @click.prevent="goTop"
        >
          <img
            :src="data.site.logo"
            alt="Centre Sud"
          >
        </a>

        <nav class="nav__links">
          <a
            v-for="item in data.nav.items"
            :key="item.anchor"
            :href="item.anchor"
            @click.prevent="goTo(item.anchor)"
          >{{ item.label }}</a>
        </nav>

        <button
          class="nav__burger"
          aria-label="Menu"
          @click="isMenuOpen = !isMenuOpen"
        >
          <img
            src="/assets/pictos/header/menu.svg"
            alt=""
          >
        </button>
      </div>
    </div>

    <transition name="menu">
      <nav
        v-if="isMenuOpen"
        class="nav__mobile"
      >
        <a
          v-for="item in data.nav.items"
          :key="item.anchor"
          :href="item.anchor"
          @click.prevent="goTo(item.anchor)"
        >{{ item.label }}</a>
        <!-- mentions légales & co : dans le menu sur mobile (retirées du pied de page) -->
        <div
          v-if="legalLinks.length"
          class="nav__mobile-legal"
        >
          <router-link
            v-for="link in legalLinks"
            :key="link.label"
            :to="link.href"
            @click="isMenuOpen = false"
          >{{ link.label }}</router-link>
        </div>
      </nav>
    </transition>
  </header>
</template>

<script>
import { openingStatus } from '@/content/openingStatus'
import { scrollToAnchor } from '@/utils/scroll'

export default {
  name: 'SiteNav',
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isScrolled: false,
      isMenuOpen: false,
      now: new Date(),
      clock: null
    }
  },
  computed: {
    // calculé depuis les horaires de la section « Plan et horaires » (Strapi)
    status() {
      return openingStatus(this.data.planHoraires?.hours, this.now)
    },
    openState() {
      return this.status ? this.status.state : 'open'
    },
    // le texte reste le libellé fixe, seule la pastille change de couleur
    openLabel() {
      return this.data.site.openLabel
    },
    // état détaillé en infobulle, pour ne pas dépendre de la seule couleur
    openTitle() {
      return this.status ? this.status.label : null
    },
    legalLinks() {
      return this.data.footer?.legalLinks || []
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true })
    this.onScroll()
    // réévalue l'état d'ouverture pour qu'il bascule sans recharger la page
    this.clock = window.setInterval(() => { this.now = new Date() }, 30000)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll)
    window.clearInterval(this.clock)
  },
  methods: {
    onScroll() {
      this.isScrolled = window.scrollY > 30
    },
    goTop() {
      this.isMenuOpen = false
      if (this.$route.path !== '/') {
        this.$router.push('/')
        return
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    goTo(anchor) {
      this.isMenuOpen = false
      if (this.$route.path !== '/') {
        this.$router.push({ path: '/', hash: anchor })
        return
      }
      scrollToAnchor(anchor)
    }
  }
}
</script>

<style lang="scss" scoped>
.nav {
  // logo : pleine taille en haut de page, réduit une fois la page défilée pour
  // garder un en-tête collant compact (utils/scroll.js lit --logo-h-compact)
  --logo-h: 124px;
  --logo-h-compact: 76px;
  position: sticky;
  top: 0;
  z-index: 50;
  background: $white;

  &.is-scrolled .nav__logo img {
    height: var(--logo-h-compact);
  }

  &.is-scrolled .nav__bar {
    box-shadow: 0 6px 20px rgba($navy, 0.1);
  }
}

.nav__topbar {
  background: $bg-light;
  display: flex;
  justify-content: flex-end;
  padding: 6px 22px;
}

.nav__open {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: $ink;

  &-dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: $status-open;
    transition: background 0.3s ease;
  }

  &.is-closing &-dot {
    background: $status-closing;
  }

  &.is-closed &-dot {
    background: $status-closed;
  }
}

.nav__bar {
  background: $white;
  transition: box-shadow 0.3s ease;
}

.nav__inner {
  display: flex;
  align-items: center;
  gap: clamp(16px, 3vw, 48px);
  padding: 14px 0;
}

.nav__logo img {
  height: var(--logo-h);
  width: auto;
  transition: height 0.3s ease;
}

.nav__links {
  display: flex;
  align-items: center;
  gap: clamp(14px, 2.4vw, 38px);
  margin-left: auto;

  a {
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: $ink;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: -6px;
      width: 0;
      height: 2px;
      background: linear-gradient(90deg, $green, $cyan);
      transition: width 0.25s ease;
    }

    &:hover::after {
      width: 100%;
    }
  }
}

.nav__burger {
  display: none;
  border: none;
  background: none;
  padding: 6px;

  img {
    width: 26px;
    height: 26px;
  }
}

// superposé sous la barre : hors du flux, sa fermeture ne décale pas la page
// (sinon le défilement vers une ancre, calculé menu ouvert, tombe trop haut)
.nav__mobile {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 20px 22px 26px;
  background: $white;
  border-top: 1px solid rgba($navy, 0.08);
  box-shadow: 0 14px 24px rgba($navy, 0.12);

  a {
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: $ink;
  }

  // mobile seulement : ailleurs ces liens restent dans le pied de page
  &-legal {
    display: none;
    flex-direction: column;
    gap: 12px;
    margin-top: 4px;
    padding-top: 18px;
    border-top: 1px solid rgba($navy, 0.1);

    a {
      font-size: 12.5px;
      font-weight: 500;
      letter-spacing: 0;
      text-transform: none;
      color: rgba($ink, 0.7);
    }

    @include mobile {
      display: flex;
    }
  }
}

.menu-enter-active,
.menu-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@include tablet-down {
  .nav {
    --logo-h: 96px;
    --logo-h-compact: 60px;
  }

  .nav__links {
    display: none;
  }

  .nav__burger {
    display: block;
    margin-left: auto;
  }
}
</style>
